import React from 'react';
import Icon from '../../../components/AppIcon';

const IncomeSummaryCards = ({ incomeSources, activeTab }) => {
  const filteredSources = activeTab === 'all' 
    ? incomeSources 
    : incomeSources.filter(source => source.type === activeTab);

  const calculateAnnualAmount = (amount, frequency) => {
    const multipliers = {
      monthly: 12,
      quarterly: 4,
      annually: 1,
      'one-time': 1
    };
    return amount * (multipliers[frequency] || 1);
  };

  const currentYear = new Date().getFullYear();
  
  const totalCurrentAnnual = filteredSources.reduce((sum, source) => {
    if (source.startYear <= currentYear && source.endYear >= currentYear) {
      return sum + calculateAnnualAmount(source.currentAmount, source.frequency);
    }
    return sum;
  }, 0);

  const totalProjectedAnnual = filteredSources.reduce((sum, source) => {
    if (source.startYear <= currentYear && source.endYear >= currentYear) {
      const yearsFromStart = currentYear - source.startYear;
      const projectedAmount = source.currentAmount * Math.pow(1 + (source.growthRate / 100), yearsFromStart);
      return sum + calculateAnnualAmount(projectedAmount, source.frequency);
    }
    return sum;
  }, 0);

  const averageGrowthRate = filteredSources.length > 0 
    ? filteredSources.reduce((sum, source) => sum + source.growthRate, 0) / filteredSources.length 
    : 0;

  const averageProbability = filteredSources.length > 0 
    ? filteredSources.reduce((sum, source) => sum + source.probability, 0) / filteredSources.length 
    : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const cards = [
    {
      title: 'Current Annual Income',
      value: formatCurrency(totalCurrentAnnual),
      subtitle: `From ${filteredSources.length} source${filteredSources.length !== 1 ? 's' : ''}`,
      icon: 'Wallet',
      color: 'primary',
      trend: null
    },
    {
      title: 'Projected Annual Income',
      value: formatCurrency(totalProjectedAnnual),
      subtitle: `With growth applied for ${currentYear}`,
      icon: 'TrendingUp',
      color: 'accent',
      trend: totalProjectedAnnual > totalCurrentAnnual ? 'up' : totalProjectedAnnual < totalCurrentAnnual ? 'down' : null
    },
    {
      title: 'Average Growth Rate',
      value: `${averageGrowthRate.toFixed(1)}%`,
      subtitle: 'Weighted across all sources',
      icon: 'BarChart3',
      color: 'warning',
      trend: averageGrowthRate > 3 ? 'up' : averageGrowthRate < 3 ? 'down' : null
    },
    {
      title: 'Confidence Level',
      value: `${averageProbability.toFixed(0)}%`,
      subtitle: 'Average probability score',
      icon: 'Target',
      color: averageProbability >= 80 ? 'success' : averageProbability >= 60 ? 'warning' : 'destructive',
      trend: null
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <div key={index} className="bg-card rounded-lg border border-border p-6 hover:shadow-elevated transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className={`
              w-12 h-12 rounded-lg flex items-center justify-center
              ${card.color === 'primary' ? 'bg-primary/10 text-primary' : ''}
              ${card.color === 'accent' ? 'bg-accent/10 text-accent' : ''}
              ${card.color === 'warning' ? 'bg-warning/10 text-warning' : ''}
              ${card.color === 'success' ? 'bg-success/10 text-success' : ''}
              ${card.color === 'destructive' ? 'bg-destructive/10 text-destructive' : ''}
            `}>
              <Icon name={card.icon} size={24} />
            </div>
            {card.trend && (
              <div className={`
                flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                ${card.trend === 'up' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}
              `}>
                <Icon name={card.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={12} />
                {card.trend === 'up' ? 'Growing' : 'Declining'}
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">{card.title}</h3>
            <p className="text-2xl font-bold text-foreground">{card.value}</p>
            <p className="text-sm text-muted-foreground">{card.subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default IncomeSummaryCards;